import { dakaiHubKeycodeToMatrixByte } from "./v80MatrixCodec";

/** 官方公版：单宏最多 38 个动作（41B = index+count+delay + 38） */
export const V80_MACRO_MAX_ACTIONS = 38;
/** 宏块 payload 长度（不含 55 AA 86 头） */
export const V80_MACRO_CHUNK_LEN = 0x29; // 41
/** UI / localStorage 槽位数（与宏列表 M0–M15 对齐） */
export const V80_MACRO_SLOT_COUNT = 16;

/** Hub 宏键码起点：M0=0xF500=62720 */
export const V80_HUB_MACRO_KEYCODE_BASE = 0xf500;
/** 快捷键行：普通改键 type */
export const V80_SHORTCUT_TYPE_KEY = 0x50;
/** 快捷键行：绑宏 type（公版 code1=208） */
export const V80_SHORTCUT_TYPE_MACRO = 0xd0;
/** 公版 code2 = 16 + macroIndex */
export const V80_SHORTCUT_MACRO_CODE2_BASE = 16;

export type V80MacroUiAction = {
  actionType?: number; // 1 鼠标 / 3 键盘
  status: number; // 1 按下 / 0 抬起
  delay?: number;
  keyCode: number;
  keyName?: string;
  code?: string;
};

export function isV80HubMacroKeycode(code: number): boolean {
  const n = Number(code) | 0;
  return (
    n >= V80_HUB_MACRO_KEYCODE_BASE &&
    n < V80_HUB_MACRO_KEYCODE_BASE + V80_MACRO_SLOT_COUNT
  );
}

export function v80HubMacroKeycodeToIndex(code: number): number {
  return (Number(code) | 0) - V80_HUB_MACRO_KEYCODE_BASE;
}

export function v80MacroIndexToHubKeycode(index: number): number {
  return V80_HUB_MACRO_KEYCODE_BASE + (index & 0x0f);
}

/** 快捷键槽 → Hub 键码；宏返回 0xF5xx，普通改键走线码映射 */
export function v80ShortcutPairToHubKeycode(
  typeByte: number,
  codeByte: number,
  wireToHub: (wire: number) => number
): number | null {
  if (typeByte === V80_SHORTCUT_TYPE_MACRO) {
    const idx = (codeByte | 0) - V80_SHORTCUT_MACRO_CODE2_BASE;
    if (idx < 0 || idx >= V80_MACRO_SLOT_COUNT) return null;
    return v80MacroIndexToHubKeycode(idx);
  }
  if (typeByte === V80_SHORTCUT_TYPE_KEY || typeByte === 0) {
    return wireToHub(codeByte & 0xff);
  }
  if (codeByte) return wireToHub(codeByte & 0xff);
  return null;
}

/**
 * 公版 `el(macroIndex, type, delayTime, list)`：
 * - [0]=槽位 [1]=动作数 [2]=delayTime/10+1（网站宏 type=2 时为 0）
 * - [3+]=线码；按下置 bit7；鼠标映射 112–116
 */
export function encodeV80MacroChunk(
  macroId: number,
  actions: V80MacroUiAction[],
  delayTimeMs = 10,
  macroType: number = 1
): number[] {
  const list = actions.slice(0, V80_MACRO_MAX_ACTIONS);
  let delayByte = 0;
  if (macroType !== 2) {
    delayByte = Math.round(Number(delayTimeMs) / 10) + 1;
    if (delayByte < 1) delayByte = 1;
    if (delayByte > 255) delayByte = 255;
  }
  const out: number[] = [macroId & 0xff, list.length & 0xff, delayByte & 0xff];
  for (const act of list) {
    let code = encodeV80MacroActionCode(act);
    if (Number(act.status) === 1 && macroType !== 2) {
      code |= 0x80;
    }
    out.push(code & 0xff);
  }
  while (out.length < V80_MACRO_CHUNK_LEN) out.push(0);
  return out.slice(0, V80_MACRO_CHUNK_LEN);
}

function encodeV80MacroActionCode(act: V80MacroUiAction): number {
  const actionType = Number(act.actionType ?? 3);
  const keyCode = Number(act.keyCode) || 0;
  if (actionType === 1) {
    return hubMouseToV80MacroCode(keyCode);
  }
  return dakaiHubKeycodeToMatrixByte(keyCode) & 0x7f;
}

function hubMouseToV80MacroCode(keyCode: number): number {
  const byButtonIndex: Record<number, number> = {
    0: 112,
    1: 114,
    2: 113,
    3: 115,
    4: 116,
  };
  if (byButtonIndex[keyCode] != null) return byButtonIndex[keyCode];
  const byMask: Record<number, number> = {
    0x01: 112,
    0x02: 113,
    0x04: 114,
    0x08: 115,
    0x10: 116,
  };
  if (byMask[keyCode] != null) return byMask[keyCode];
  if (keyCode >= 112 && keyCode <= 116) return keyCode;
  return 112;
}

/** 从动作列表推断公版 delayTime（ms），默认 10 */
export function inferV80MacroDelayTimeMs(actions: V80MacroUiAction[]): number {
  const delays = actions
    .map((a) => Number(a.delay) || 0)
    .filter((d) => d > 0 && d <= 2540);
  if (!delays.length) return 10;
  delays.sort((a, b) => a - b);
  return delays[Math.floor(delays.length / 2)] || 10;
}
